import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

// Async thunk to fetch Privacy Policy
export const fetchPrivacyPolicy = createAsyncThunk(
  'policy/fetchPrivacyPolicy',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/privacy-policy`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch Privacy Policy');
    }
  }
);

// Async thunk to fetch Terms & Conditions
export const fetchTermsConditions = createAsyncThunk(
  'policy/fetchTermsConditions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/terms-conditions`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch Terms & Conditions');
    }
  }
);

// Async thunk to update Privacy Policy
export const updatePrivacyPolicy = createAsyncThunk(
  'policy/updatePrivacyPolicy',
  async (content, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/privacy-policy`, { content });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update Privacy Policy');
    }
  }
);

// Async thunk to update Terms & Conditions
export const updateTermsConditions = createAsyncThunk(
  'policy/updateTermsConditions',
  async (content, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/terms-conditions`, { content });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update Terms & Conditions');
    }
  }
);

const privacyPolicyTermsSlice = createSlice({
  name: 'policy',
  initialState: {
    privacyPolicy: null,
    termsConditions: null,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Privacy Policy
      .addCase(fetchPrivacyPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrivacyPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.privacyPolicy = action.payload.data.content;
      })
      .addCase(fetchPrivacyPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Terms & Conditions
      .addCase(fetchTermsConditions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTermsConditions.fulfilled, (state, action) => {
        state.loading = false;
        state.termsConditions = action.payload.data.content;
      })
      .addCase(fetchTermsConditions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Privacy Policy
      .addCase(updatePrivacyPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrivacyPolicy.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.successMessage = 'Privacy Policy updated successfully';
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updatePrivacyPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Terms & Conditions
      .addCase(updateTermsConditions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTermsConditions.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.successMessage = 'Terms & Conditions updated successfully';
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updateTermsConditions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearMessages } = privacyPolicyTermsSlice.actions;

export default privacyPolicyTermsSlice.reducer;